import { cn, Input, InputProps } from '@heroui/react'
import type { FieldProps } from 'formik'
import { Field } from 'formik'
import { motion } from 'framer-motion'
import { forwardRef } from 'react'

export interface InputFormikProps extends Omit<InputProps, 'classNames'> {
  label?: string
  placeholder?: string
  classNameContainer?: string
  variant?: 'bordered' | 'flat' | 'faded' | 'underlined'
  classNames?: {
    label?: string[]
    input?: string[]
    innerWrapper?: string
    inputWrapper?: string[]
  }
  labelPlacement?: 'outside' | 'inside' | 'outside-left'
  name: string
  isRequired?: boolean
}

const InputFormik = forwardRef<HTMLInputElement, InputFormikProps>(
  (
    {
      classNameContainer,
      variant = 'bordered',
      classNames,
      labelPlacement = 'outside',
      name,
      isRequired = false,
      ...props
    },
    ref,
  ) => {
    return (
      <Field name={name}>
        {({ field, meta }: FieldProps) => {
          return (
            <motion.div className={cn(classNameContainer)}>
              <Input
                {...field}
                ref={ref}
                fullWidth
                className="h-full"
                errorMessage={meta.error || meta.touched ? meta.error : ''}
                isRequired={isRequired}
                labelPlacement={labelPlacement}
                radius="sm"
                value={typeof field.value === 'string' ? field.value : ''}
                variant={variant}
                classNames={{
                  label: [
                    'text-gray-900',
                    'dark:text-foreground',
                    'group-data-[filled-within=true]:text-gray-900',
                    'dark:group-data-[filled-within=true]:text-white',
                    'group-data-[invalid=true]:text-danger',
                    'dark:group-data-[invalid=true]:text-danger',
                  ],
                  input: [
                    'bg-transparent',
                    'text-gray-900',
                    'dark:text-white',
                    'placeholder:text-gray-500',
                    'dark:placeholder:text-white/60',
                    'group-data-[invalid=true]:placeholder:text-danger',
                  ],
                  innerWrapper: 'bg-transparent',
                  inputWrapper: [
                    'bg-gray-50',
                    'dark:bg-[#1e1e20]',
                    'border-gray-200',
                    'dark:border-default-600',
                    'hover:bg-gray-100',
                    'dark:hover:bg-[#1e1e20]/70',
                    'group-data-[focus=true]:bg-gray-100',
                    'dark:group-data-[focus=true]:bg-[#1e1e20]/50',
                    'group-data-[focus=true]:border-primary',
                    'dark:group-data-[focus=true]:border-primary',
                    '!cursor-text',
                  ],
                  ...classNames,
                }}
                {...props}
              />
            </motion.div>
          )
        }}
      </Field>
    )
  },
)

InputFormik.displayName = 'InputFormik'

export default InputFormik
